import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { capitalize, getIcon } from "../constants/constants";
import { getMeetingByUuid, getTaskById } from "../features/admin/adminSlice";

function MeetingDetail() {
  const { uuid } = useParams();
  const dispatch = useDispatch();
  const {
    selectedMeeting: meeting,
    tasks,
    loading,
  } = useSelector((state) => state.admin);

  useEffect(() => {
    dispatch(getMeetingByUuid(uuid));
    dispatch(getTaskById(uuid));
  }, [dispatch, uuid]);

  if (loading) return <div className="p-4">Loading...</div>;
  if (!meeting) return <div className="p-4">Meeting not found</div>;

  return (
    <div className="content mt-6">
      <div className="container-fluid">
        <h3 className="fw-bold mb-4">Meeting Details</h3>

        <div className="card p-4 mb-4">
          <h5 className="mb-4">Meeting Information</h5>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">Meeting Title</label>
              <input className="form-control" value={meeting.title} disabled />
            </div>

            <div className="col-md-6 mb-3">
              <label className="form-label">Status</label>
              <input className="form-control" value={meeting.status} disabled />
            </div>

            <div className="col-md-6 mb-3">
              <label className="form-label">Agent</label>
              <input
                className="form-control"
                value={meeting?.agent?.name || "-"}
                disabled
              />
            </div>

            <div className="col-md-6 mb-3">
              <label className="form-label">Created At</label>
              <input
                className="form-control"
                value={moment(meeting.createdAt).format("LLL")}
                disabled
              />
            </div>
          </div>
        </div>

        <div className="card p-4 mb-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h5 className="mb-0">Participants</h5>

            <span className="badge bg-primary">
              {meeting?.participants?.length || 0} Participants
            </span>
          </div>

          {meeting?.participants?.length ? (
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0">
                <thead className="table">
                  <tr>
                    <th>Name</th>
                    <th>Email / Username</th>
                    <th>Platform</th>
                    <th>Added</th>
                  </tr>
                </thead>

                <tbody>
                  {meeting.participants.map((p) => (
                    <tr key={p.uuid || p}>
                      <td>
                        <div className="d-flex align-items-center gap-3">
                          <div
                            className="rounded-circle bg-primary-subtle d-flex align-items-center justify-content-center fw-bold"
                            style={{
                              width: 38,
                              height: 38,
                            }}
                          >
                            {(p.name || "?").charAt(0).toUpperCase()}
                          </div>

                          <div>
                            <div className="fw">{p.name}</div>
                          </div>
                        </div>
                      </td>

                      <td>{p.email || p.username || "-"}</td>

                      <td>
                        <span className="badge bg-secondary">
                          {p.platform || "-"}
                        </span>
                      </td>

                      <td>
                        {p.createdAt
                          ? moment(p.createdAt).format("MMM D, YYYY")
                          : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-muted">No participants identified</div>
          )}
        </div>

        <div className="card p-4 mb-4">
          <h5 className="mb-4">Tasks Identified</h5>

          {tasks?.length ? (
            (() => {
              const groupedTasks = tasks.reduce((acc, action) => {
                const platform =
                  action?.settingId?.assistantId?.platform?.type || "other";

                if (!acc[platform]) {
                  acc[platform] = [];
                }

                acc[platform].push(action);
                return acc;
              }, {});

              return Object.entries(groupedTasks).map(
                ([platform, platformTasks]) => {
                  const iconSrc = getIcon(platform);

                  return (
                    <div key={platform} className="mb-4">
                      <div className="d-flex align-items-center gap-2 mb-3 border-bottom pb-2">
                        {iconSrc ? (
                          <img
                            src={iconSrc}
                            alt={platform}
                            style={{
                              width: "16px",
                              height: "16px",
                              objectFit: "contain",
                            }}
                          />
                        ) : (
                          <i className="fas fa-plug text-primary" />
                        )}

                        <h6 className="mb-0 fw-bold">{capitalize(platform)}</h6>
                      </div>

                      {platformTasks.map((action) => (
                        <div
                          key={action.uuid}
                          className="border rounded p-3 mb-3"
                        >
                          <div className="d-flex justify-content-between mb-2">
                            <strong>{action.name}</strong>

                            <span
                              className={`badge ${
                                action.status === "completed"
                                  ? "bg-success"
                                  : action.status === "failed"
                                    ? "bg-danger"
                                    : "bg-warning"
                              }`}
                            >
                              {capitalize(action.status)}
                            </span>
                          </div>

                          <p className="mb-2">{action.description}</p>

                          <div className="small text-muted d-flex flex-wrap gap-2">
                            <span>
                              Operation:
                              <strong className="ms-1">
                                {action.operation || "-"}
                              </strong>
                            </span>

                            {action.priority && (
                              <>
                                <span>•</span>
                                <span>
                                  Priority:
                                  <strong className="ms-1">
                                    {action.priority}
                                  </strong>
                                </span>
                              </>
                            )}
                          </div>

                          {action.labels?.length > 0 && (
                            <div className="mt-3">
                              {action.labels.map((label) => (
                                <span
                                  key={label}
                                  className="badge bg-secondary me-2"
                                >
                                  {label}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                },
              );
            })()
          ) : (
            <p className="text-muted mb-0">No tasks identified</p>
          )}
        </div>

        <div className="card p-4 mb-4">
          <h5 className="mb-3">Meeting Summary</h5>

          <div
            className="border rounded p-3"
            style={{
              minHeight: "150px",
              maxHeight: "500px",
              overflowY: "auto",
            }}
          >
            <pre
              className="mb-0"
              style={{
                whiteSpace: "pre-wrap",
                fontFamily: "inherit",
              }}
            >
              {meeting.summary || "No summary available"}
            </pre>
          </div>
        </div>

        <div className="card p-4 mb-5">
          <h5 className="mb-3">Transcript</h5>

          <div
            className="border rounded p-3"
            style={{
              minHeight: "150px",
              maxHeight: "500px",
              overflowY: "auto",
            }}
          >
            <pre
              className="mb-0"
              style={{
                whiteSpace: "pre-wrap",
                fontFamily: "inherit",
              }}
            >
              {meeting.transcript}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MeetingDetail;
